import { Dialog, LinearProgress } from '@mui/material'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import config from '../../config.json'
import { DropAlert } from '../drops/DropAlert'
import { DropPaper } from '../drops/DropPaper'
import { sleep } from '../helpers/FormatLinks'
import { getBoostAction } from '../helpers/WaxApi'
import { ImageUploadButton } from '../imageupload/ImageUploadButton'
import { useSharedState } from '../waxplorer/Waxplorer'
import MainButton from '../common/util/input/MainButton'
import InputField from '../common/util/InputField'

export const UpdateCollectionLogoDialog = ({
    open,
    setOpen,
    atomicCollectionData,
    updateAtomicCollectionData = () => {},
}) => {
    const { t } = useTranslation('common')

    const [imageLoaded, setImageLoaded] = useState(false)
    const [logo, setLogo] = useState(null)
    const [imageUploadError, setImageUploadError] = useState(null)
    const [uploadProgress, setUploadProgress] = useState(null)
    const [isUploading, setIsUploading] = useState(false)

    const [isSaving, setIsSaving] = useState(false)
    const [error, setError] = useState(null)
    const [transactionId, setTransactionId] = useState(null)
    const [state] = useSharedState()
    const activeUser = state?.activeUser

    const userName = activeUser?.accountName
        ? state?.activeUser.accountName
        : null

    const saveCollection = async () => {
        setIsSaving(true)
        setError(null)
        setTransactionId(null)

        const collectionData = atomicCollectionData.data.data

        const actions = [getBoostAction(activeUser)]

        const data = []

        data.push({
            key: 'description',
            value: ['string', collectionData.description],
        })

        data.push({
            key: 'name',
            value: ['string', collectionData.name],
        })

        data.push({
            key: 'img',
            value: ['string', collectionData.img],
        })

        data.push({
            key: 'url',
            value: ['string', collectionData.url],
        })

        data.push({
            key: 'socials',
            value: ['string', collectionData.socials],
        })

        data.push({
            key: 'creator_info',
            value: ['string', collectionData.creator_info],
        })

        let temp
        if (collectionData.images) {
            temp = JSON.parse(collectionData.images)
            temp.logo_512x512 = logo.url
        } else {
            temp = { logo_512x512: logo.url, banner_1920x500: '' }
        }

        data.push({
            key: 'images',
            value: ['string', JSON.stringify(temp)],
        })

        actions.push({
            account: 'atomicassets',
            name: 'setcoldata',
            authorization: [
                {
                    actor: userName,
                    permission: activeUser.requestPermission
                        ? activeUser.requestPermission
                        : 'active',
                },
            ],
            data: {
                collection_name: atomicCollectionData.data.collection_name,
                data: data,
            },
        })

        let saveError = null
        let saveTransactionId = null

        try {
            const result = await activeUser.session.transact(
                {
                    actions: actions,
                },
                {
                    expireSeconds: 300,
                    blocksBehind: 0,
                },
            )

            saveTransactionId = result?.response?.transaction_id
        } catch (e) {
            saveError = e.message
            setError(saveError)
            setIsSaving(false)
        }
        await sleep(3000)
        updateAtomicCollectionData()
        setTransactionId(saveTransactionId)
        setIsSaving(false)
    }

    useEffect(() => {
        if (imageLoaded) {
            const img = document.getElementById('logo_img')

            const width = img.naturalWidth
            const height = img.naturalHeight

            setLogo({
                url: logo.url,
                width: width,
                height: height,
                wrongSize: width !== 512 || height !== 512,
                broken: false,
            })
            setImageLoaded(false)
        }
    }, [imageLoaded])

    const useOnChainImage = () => {
        if (atomicCollectionData?.data?.data?.images) {
            const parsedImages = JSON.parse(
                atomicCollectionData.data.data.images,
            )
            const url = parsedImages.logo_512x512
                .trim()
                .replaceAll(config.ipfs, '')
            if (parsedImages.logo_512x512) {
                setLogo({
                    url: url,
                    width: null,
                    height: null,
                    wrongSize: null,
                    broken: false,
                })
            }
        } else {
            setLogo({
                url: null,
                width: null,
                height: null,
                wrongSize: null,
                broken: false,
            })
        }
    }

    useEffect(() => {
        useOnChainImage()
    }, [atomicCollectionData])

    const close = () => {
        if (!isSaving) {
            setOpen(false)
            useOnChainImage()
            setError(null)
            setTransactionId(null)
        }
    }

    return (
        <Dialog open={open} onClose={() => close()} maxWidth="md" fullWidth>
            <DropPaper title={t('collection.update_logo')} noMargin>
                {atomicCollectionData === null ? (
                    <div>
                        <div className="font-lg italic">
                            Loading existing collection data...
                        </div>
                        <div className="my-5">
                            <LinearProgress />
                        </div>
                    </div>
                ) : isSaving ? (
                    <div>
                        <div className="font-lg italic">
                            Setting your new logo image...
                        </div>
                        <div className="my-5">
                            <LinearProgress />
                        </div>
                    </div>
                ) : (
                    <div>
                        <div className="text-center italic mb-2">Preview</div>
                        <div className="flex justify-center mb-5">
                            <div className="w-[512px] h-[512px] bg-black relative ">
                                {isUploading ? (
                                    <div className="relative top-1/2 -translate-y-1/2">
                                        <LinearProgress
                                            variant="determinate"
                                            value={uploadProgress}
                                        />
                                    </div>
                                ) : logo && logo.broken === false ? (
                                    <img
                                        id="logo_img"
                                        src={config.ipfs + logo.url}
                                        onError={() => {
                                            const temp = structuredClone(logo)
                                            temp.broken = true
                                            setLogo(temp)
                                        }}
                                        onLoad={() => setImageLoaded(true)}
                                    />
                                ) : logo &&
                                  logo.url?.length > 0 &&
                                  logo.broken === true ? (
                                    <div className="relative top-1/2 -translate-y-1/2 text-center font-bold italic">
                                        - Invalid Image -
                                    </div>
                                ) : null}
                            </div>
                        </div>
                        <div className="flex justify-center mb-2 gap-4">
                            <div className="w-132">
                                <InputField
                                    type="text"
                                    variant="bordered"
                                    size="md"
                                    label="Logo Hash"
                                    value={logo?.url ? logo.url : ''}
                                    onChange={(e) => {
                                        const temp = structuredClone(logo)
                                        temp.url = e.target.value.trim()
                                        temp.width = null
                                        temp.height = null
                                        temp.broken = false
                                        setLogo(temp)
                                    }}
                                    description={
                                        logo?.url
                                            ? ''
                                            : 'Image needs to be 512x512'
                                    }
                                    isDisabled={isUploading || transactionId}
                                    isInvalid={logo?.broken || logo?.wrongSize}
                                />
                            </div>
                            <ImageUploadButton
                                id="logo-upload"
                                setIpfsHash={(hash) => {
                                    const temp = structuredClone(logo)
                                    temp.url = hash
                                    temp.width = null
                                    temp.height = null
                                    temp.broken = false
                                    setLogo(temp)
                                }}
                                disabled={transactionId}
                                setError={setImageUploadError}
                                setProgressPercentage={setUploadProgress}
                                setIsUploading={setIsUploading}
                            />
                        </div>
                        <DropAlert
                            condition={
                                logo?.wrongSize &&
                                !logo?.broken &&
                                logo?.url?.length > 0
                            }
                            severity="error"
                            className="flex justify-center"
                        >
                            Incorrect dimensions! Logo needs to be 512x512, but
                            the selected image is {logo?.width}x{logo?.height}
                        </DropAlert>
                        <DropAlert
                            condition={imageUploadError !== null}
                            severity="error"
                            className="flex justify-center"
                            title="An error occured during image upload"
                        >
                            {imageUploadError}
                        </DropAlert>
                        <DropAlert
                            condition={error !== null}
                            severity="error"
                            title="An error occured!"
                        >
                            {error}
                        </DropAlert>
                        <DropAlert
                            condition={transactionId !== null}
                            transactionId={transactionId}
                            severity="success"
                            title="Image successfully updated!"
                        >
                            Please note that it may take a couple of minutes to
                            see your changes reflected on the page
                        </DropAlert>
                        <div className="flex flex-wrap justify-center mt-5 gap-4">
                            <MainButton
                                className=""
                                fullWidth={false}
                                size="md"
                                onClick={() => saveCollection()}
                                isDisabled={
                                    transactionId ||
                                    isSaving ||
                                    !logo ||
                                    logo.broken ||
                                    logo.wrongSize ||
                                    !logo.url
                                }
                            >
                                Save
                            </MainButton>
                            <MainButton
                                className=""
                                fullWidth={false}
                                color="danger"
                                size="md"
                                onClick={() => close()}
                                isDisabled={isSaving}
                            >
                                Close
                            </MainButton>
                        </div>
                    </div>
                )}
            </DropPaper>
        </Dialog>
    )
}
